import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronLeft, FaChevronRight, FaCar, FaBox, FaWeightHanging, FaShippingFast } from 'react-icons/fa';
import Spinner from '../common/Spinner';
import './MyProfile.css';

const HistoryPosts = ({ archivedPosts, archivedPackages, isLoading }) => {
  const { t } = useTranslation('common');
  const [page, setPage] = useState(1);
  const postsPerPage = 3;
  
  // Combine archived posts and packages for pagination
  const allArchivedPosts = [
    ...(archivedPosts || []).map(post => ({ ...post, isPackage: false })),
    ...(archivedPackages || []).map(pkg => ({ ...pkg, isPackage: true }))
  ];

  // Sort by creation date (newest first)
  allArchivedPosts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Get paginated posts
  const getPaginatedPosts = () => {
    const startIndex = (page - 1) * postsPerPage;
    const endIndex = startIndex + postsPerPage;
    return allArchivedPosts.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(allArchivedPosts.length / postsPerPage);
  const paginatedPosts = getPaginatedPosts();

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return t('posts.not_specified');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return dateString || t('posts.not_specified');
    }
  };

  const formatAddress = (item) => {
    // Check for newer address format first
    if (item.address_from_city || item.address_from_street) {
      const parts = [item.address_from_street, item.address_from_city, item.address_from_country]
        .filter(Boolean);
      return parts.join(', ') || t('posts.not_specified');
    }
    // Fallback to old format
    return item.address_from || t('posts.not_specified');
  };

  const formatAddressTo = (item) => {
    // Check for newer address format first
    if (item.address_to_city || item.address_to_street) {
      const parts = [item.address_to_street, item.address_to_city, item.address_to_country]
        .filter(Boolean);
      return parts.join(', ') || t('posts.not_specified');
    }
    // Fallback to old format
    return item.address_to || t('posts.not_specified');
  };

  if (isLoading) {
    return (
      <div className="HistoryPosts loading">
        <h3>{t('posts.history')}:</h3>
        <Spinner size="medium" />
      </div>
    );
  }

  return (
    <div className="HistoryPosts">
      <h3>{t('posts.history')}:</h3>
      {allArchivedPosts.length > 0 ? (
        <>
          {paginatedPosts.map((item) => (
            <div 
              key={`${item.isPackage ? 'pkg' : 'post'}-${item.id}`} 
              className={`Hpost ${item.isPackage ? 'package-post' : ''}`}
            >
              <div className="post-header">
                <span className="post-role">
                  {item.isPackage ? 
                    t('posts.package') : 
                    t(`posts.${item.role.toLowerCase()}`)
                  }
                </span>
              </div>
              
              {/* Regular post details */}
              {!item.isPackage && (
                <>
                  {item.car && <div><strong>{t('posts.car')}:</strong> {item.car}</div>}
                  {item.seats && <div><strong>{t('posts.seats')}:</strong> {item.seats}</div>}
                </>
              )}
              
              {/* Package-specific details */}
              {item.isPackage && (
                <>
                  <div className="package-details">
                    <div>
                      <FaBox className="package-icon" />
                      <strong>{t('posts.package_size')}:</strong> {item.package_size}
                    </div>
                    <div>
                      <FaWeightHanging className="package-icon" />
                      <strong>{t('posts.package_weight')}:</strong> {item.package_weight}
                    </div>
                    <div>
                      <FaShippingFast className="package-icon" />
                      <strong>{t('posts.urgency')}:</strong> {item.delivery_urgency}
                    </div>
                  </div>
                </>
              )}
              
              {/* Common details */}
              <div><strong>{t('posts.address_from')}:</strong> {formatAddress(item)}</div>
              <div><strong>{t('posts.address_to')}:</strong> {formatAddressTo(item)}</div>
              
              <div className="post-time-details">
                <div><strong>{t('posts.starting_time')}:</strong> {item.travel_time || t('posts.not_specified')}</div>
                <div><strong>{t('posts.starting_date')}:</strong> {formatDate(item.travel_date)}</div>
              </div>
              
              {!item.isPackage && (
                <div><strong>{t('posts.drive_type')}:</strong> {t(`posts.${item.travel_type.toLowerCase().replace('-', '_')}`)}</div>
              )}
            </div>
          ))}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <button 
                className="pagination-button"
                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                disabled={page === 1}
              >
                <FaChevronLeft />
              </button>
              
              <div className="pagination-info">
                {t('pagination.page')} {page} / {totalPages}
              </div>
              
              <button 
                className="pagination-button"
                onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                disabled={page === totalPages}
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="no-posts-message">{t('posts.no_archived')}</p>
      )}
    </div>
  );
};

export default HistoryPosts;